import React, { useContext, useEffect } from "react";
import { Button, Card, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
// import DatePicker from "react-datepicker";
import HeaderWithTitle from "../Shared/Components/HeaderWithTitle";
import { AuthContext } from "../Auth/AuthProvider";
import { abpnClient } from "../Services/AbpnClient";
import AbpnSpinner from "../Shared/Components/AbpnSpinner";
import { ValidatorHelper } from "../Shared/Utility/ValidatorHelper";
import { ProfileContext } from "../Auth/ProfileProvider";
import { configService } from "../Services/Config/ConfigService";

var hasOnlyNumbersRegex = /^\d+$/;

export function Reports() {
    const authContext = React.useContext(AuthContext);
    const { user } = useContext(ProfileContext);
    const trainingProgram = user.trainingPrograms.find(x => x.trainingProgramId === user.selectedTrainingProgramId);

    const [isDownloadingReport, setIsDownloadingReport] = React.useState<boolean>(false);
    const [isDownloadingDetailReport, setIsDownloadingDetailReport] = React.useState<boolean>(false);

    const [isFormValid, setIsFormValid] = React.useState<boolean>(null);
    const [startDate, setStartDate] = React.useState<string>('');
    const [endDate, setEndDate] = React.useState<string>('');
    const dateFormRef = React.useRef<HTMLFormElement>(null);
    const startDateRef = React.useRef<HTMLInputElement>(null);
    const endDateRef = React.useRef<HTMLInputElement>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [displayGprFlag, setDisplayGprFlag] = React.useState<boolean>(false);

    useEffect(() => {
        const getGprFlag = async () => {
            setLoading(true);
            var configuration = await configService.getConfiguration();
            setDisplayGprFlag(configuration.DisplayGprReports);
            setLoading(false);
        };
        getGprFlag();
    }, []);


    const updateStartDate = () => {
       setStartDate(startDateRef.current.value);
       validateStartDate();
    }

    const validateStartDate = () => {
        const element = startDateRef.current;
        ValidatorHelper.clearCustomValidation(element);

        if (!hasOnlyNumbersRegex.test(startDate)) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Enter numbers only.");
        }

        if (startDate === null || startDate === '') {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Start date is required.");
        }
    }

    const updateEndDate = () => {
        setEndDate(endDateRef.current.value);
        validateEndDate();
    }

    const validateEndDate = () => {
        const element = endDateRef.current;
        ValidatorHelper.clearCustomValidation(element);

        if (!hasOnlyNumbersRegex.test(endDate)) {
            ValidatorHelper.setCustomValidationWithFeedback(element, "Enter numbers only.");
        }

        if (endDate === null || endDate === '') {
            ValidatorHelper.setCustomValidationWithFeedback(element, "End date is required.");
        }
    }

    const downloadReport = async () => {
        // Download Graduate Peformance Report
        setIsDownloadingReport(true);

        const url = `/api/Reports/DownloadGraduatePerformanceReport?trainingProgramCode=${trainingProgram.trainingProgramCode}&trainingProgramName=${trainingProgram.trainingProgramName1}`;

        var response = await abpnClient.getResponse(url, authContext)

        var blob = await response.blob();
        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );

        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute('download', 'GraduatePerformanceReport.pdf');

        // Append to html link element page, start the download, then clean up and remove the link.
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        setIsDownloadingReport(false);
    }

    const downloadDetailReport = async () => {
        const form = dateFormRef.current;

        validateStartDate();
        validateEndDate();

        if (!form.checkValidity()) {
            setIsFormValid(false);
            return;
        }

        setIsFormValid(true);

        // Download Graduate Peformance Report
        setIsDownloadingDetailReport(true);
        const url = `/api/Reports/DownloadGraduatePerformanceDetailReport?trainingProgramCode=${trainingProgram.trainingProgramCode}&trainingProgramName=${trainingProgram.trainingProgramName1}&fromYear=${startDate}&toYear=${endDate}`;

        var response = await abpnClient.getResponse(url, authContext)

        var blob = await response.blob();
        const downloadFileUrl = window.URL.createObjectURL(
            new Blob([blob]),
        );

        const link = document.createElement('a');
        link.href = downloadFileUrl;
        link.setAttribute('download', 'GraduatePerformanceReport.pdf');

        // Append to html link element page, start the download, then clean up and remove the link.
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);

        setIsDownloadingDetailReport(false);
    }

    if (loading) return <AbpnSpinner />;

    return (
        <React.Fragment>
            <HeaderWithTitle title="Reports" subTitle="" />

            {!displayGprFlag &&
                <h6>Graduate Performance Reports are coming soon!</h6>
            }

            {displayGprFlag && !user.userRole.isProgramDirector && <h6>You must be a Program Director to view this page.</h6>}

            {displayGprFlag && user.userRole.isProgramDirector &&
                <Card className="border-pc-secondary-grey mt-4">
                    <Card.Title className="m-4 fw-bolder">
                        <span className="text-primary">Graduate Performance</span>
                        <hr className="mb-1"></hr>
                    </Card.Title>
                    <Card.Body>
                        <h5 className="fw-bolder mx-2 text-secondary">Summary
                            <OverlayTrigger placement={"right"} overlay={<Tooltip id="graduatePerformanceReportSummaryInfo" bsPrefix="pc-tooltip">This report contains your program's summary-level performance on the ABPN specialty certification examination for your chosen time frame. Included in the report are exam year and first year eligible summaries, program performance comparison with the national average performance, and breakdown by first-time takers and repeaters.</Tooltip>}>
                                <i className="las la-info-circle mx-1 text-pc-secondary"></i>
                            </OverlayTrigger>
                        </h5>
                        <Button variant="primary" className="mx-2" disabled={isDownloadingReport} type="button" onClick={() => downloadReport()} tabIndex={1}>
                            { isDownloadingReport ? <AbpnSpinner /> : <React.Fragment><i className="la la-download"></i> Download</React.Fragment> }
                        </Button>
                    </Card.Body>
                    <Card.Body>
                        <h5 className="fw-bolder mx-2 mt-1 mb-2 text-secondary">Detail
                            <OverlayTrigger placement={"right"} overlay={<Tooltip id="graduatePerformanceReportDetailInfo" bsPrefix="pc-tooltip">This report contains your program's resident-level performance on the ABPN specialty certification examination for your chosen time frame. Included in the report are resident name, last year of training, first year eligible, first-time taker/repeater, and pass/fail grade.</Tooltip>}>
                                <i className="las la-info-circle mx-1 text-pc-secondary"></i>
                            </OverlayTrigger>
                        </h5>
                        <Form ref={dateFormRef} className="mx-2" noValidate validated={isFormValid === false}>
                            <div className="row mb-1">
                                <div className="col-2">
                                    <Form.Group>
                                        <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">From Graduation Year<span className="text-primary ml-2"> *</span></Form.Label>
                                        <Form.Control ref={startDateRef} type="input" tabIndex={1} placeholder="YYYY" onChange={(_: any) => updateStartDate() }></Form.Control>
                                        <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="col-2">
                                    <Form.Group>
                                        <Form.Label className="fw-bolder mb-1 pc-font-size-smaller">To Graduation Year<span className="text-primary ml-2"> *</span></Form.Label>
                                        <Form.Control ref={endDateRef} type="input" tabIndex={2} placeholder="YYYY" onChange={(_: any) => updateEndDate() }></Form.Control>
                                        <Form.Control.Feedback type="invalid"></Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                            </div>
                                <Button variant="primary" className="mt-2" disabled={isDownloadingDetailReport} type="button" onClick={() => downloadDetailReport()} tabIndex={1}>
                                    { isDownloadingDetailReport ? <AbpnSpinner /> : <React.Fragment><i className="la la-download"></i> Download</React.Fragment> }
                                </Button>
                        </Form>
                    </Card.Body>
                </Card>
            }
        </React.Fragment>
    );
}
